import { useState } from "react";
import { categories, hotEvents, type Category } from "@/mocks/events";
import EventRow from "./EventRow";
import SectionHeading from "./SectionHeading";

interface HotListProps {
  selected: Category;
  onSelect: (name: Category) => void;
}

export default function HotList({ selected, onSelect }: HotListProps) {
  const [keyword, setKeyword] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = hotEvents.filter((e) => {
    const inCat = selected === "全部" || e.category === selected;
    const q = keyword.trim().toLowerCase();
    const inKw = !q || e.title.toLowerCase().includes(q) || e.summary.toLowerCase().includes(q) || e.tags.some((t) => t.toLowerCase().includes(q));
    return inCat && inKw;
  });

  const toggle = (id: string) => setExpandedId((cur) => (cur === id ? null : id));

  return (
    <section id="hot" className="py-12 md:py-16">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="今日热榜"
          title="正在发酵的热点事件"
          description="热榜按当前讨论热度排序，点开任意一条即可查看从起因到结果的完整时间线，标注清楚的信息来源与待证实状态。"
          action={
            <div className="relative w-full sm:w-72">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-foreground-500" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索事件 / 关键词"
                aria-label="搜索事件"
                className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>
          }
        />

        <div className="mt-6 flex flex-wrap items-center gap-2" role="group" aria-label="按分类筛选">
          {categories.map((c) => {
            const active = selected === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => onSelect(c)}
                aria-pressed={active}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
          <span>
            共 <span className="font-semibold text-primary-700">{filtered.length}</span> 起事件{keyword ? ` · 搜索「${keyword}」` : ""}
          </span>
          <span className="hidden items-center gap-1.5 sm:inline-flex">
            <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
            按热度排序 · 每 2 小时刷新
          </span>
        </div>

        <div className="mt-4 flex flex-col gap-3">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
              <i className="ri-search-eye-line text-4xl text-background-400" />
              <p className="mt-4 text-sm font-medium text-foreground-700">没找到匹配「{keyword}」的事件</p>
              <p className="mt-1 text-xs text-foreground-500">换个关键词，或回到「全部」频道再看看</p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  onSelect("全部");
                }}
                className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            </div>
          ) : (
            filtered.map((e, i) => (
              <EventRow key={e.id} event={e} index={i} open={expandedId === e.id} onToggle={() => toggle(e.id)} />
            ))
          )}
        </div>
      </div>
    </section>
  );
}